<template>
    <div class="comments">
        <h2 class="comments-title">
            Reacties <span v-if="comments.length" :key="comments.length" class="comments-count">({{ comments.length }})</span>
        </h2>

        <transition :css="false" @enter="expand" @leave="collapse">
            <p v-if="!comments.length" class="comments-empty">Nog geen reacties. Jij bent de eerste!</p>
        </transition>

        <transition-group tag="ol" class="comments-list list-unstyled" :css="false" @enter="expand" @leave="collapse">
            <comment-item v-for="comment in childrenOf(null)" :key="comment.id" :comment="comment" :depth="0"/>
        </transition-group>

        <comment-form/>
    </div>
</template>

<script setup>
import {nextTick, provide, ref, watch} from 'vue';
import CommentForm from './CommentForm.vue';
import CommentItem from './CommentItem.vue';
import {collapse, expand, reducedMotion} from './motion.js';

const props = defineProps({
    endpoint:        {type: String, required: true},
    initialComments: {type: Array, default: () => []},
    canModerate:     {type: Boolean, default: false},
    maxVisualDepth:  {type: Number, default: 1},
    staffName:       {type: String, default: null},
    recaptchaKey:    {type: String, default: ''}
});

// Het aantal veranderde (geplaatst of verwijderd); comments.js werkt daarmee het aantal in de header bij.
const emit = defineEmits(['count-change']);

// Alle reacties plat, ook antwoorden; de boom ontstaat uit parentId.
const comments = ref(props.initialComments.map((comment) => ({...comment, isNew: false})));

watch(() => comments.value.length, (count) => emit('count-change', count));

// De naam die een bezoeker als laatste invulde, zodat hij bij een volgend antwoord al klaarstaat.
const rememberedName = ref(props.staffName || '');

const childrenOf = (parentId) => comments.value.filter((comment) => comment.parentId === parentId);

const descendantIds = (id) => childrenOf(id).flatMap((child) => [child.id, ...descendantIds(child.id)]);

const nameOf = (id) => comments.value.find((comment) => comment.id === id)?.name;

// Scrolt naar een reactie en laat hem even oplichten; voor "in reactie op" voorbij het laatste niveau.
const flashedId   = ref(null);
const showComment = (id) => {
    // De kaart zelf, niet de hele draad: daar staan alle antwoorden in, en die kan hoger zijn dan het scherm.
    document.querySelector('#reactie-' + id + ' > .comment')?.scrollIntoView({behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center'});
    flashedId.value = null;
    nextTick(() => flashedId.value = id);
};

const addComment = (comment) => comments.value.push({...comment, isNew: true});

const replaceComment = (comment) => comments.value = comments.value.map((item) => item.id === comment.id ? {...comment, isNew: false} : item);

// De antwoorden eronder verdwijnen mee, net als op de server (Comment::deleteWithReplies()).
const removeComment = (id) => {
    const removed = new Set([id, ...descendantIds(id)]);
    comments.value = comments.value.filter((comment) => !removed.has(comment.id));
};

provide('comments', {
    endpoint:       props.endpoint,
    canModerate:    props.canModerate,
    maxVisualDepth: props.maxVisualDepth,
    staffName:      props.staffName,
    recaptchaKey:   props.recaptchaKey,
    rememberedName,
    flashedId,
    childrenOf,
    descendantIds,
    nameOf,
    showComment,
    addComment,
    replaceComment,
    removeComment
});
</script>
